<template>
  <div class="overflow-hidden">
    <section class="cp-page py-4">
      <header-help :is-loading="isLoading"></header-help>
      
      <!-- Background animated orbs using Stella brand colors -->
      <div class="cp-bg-orb cp-bg-orb--1"></div>
      <div class="cp-bg-orb cp-bg-orb--2"></div>

      <div class="container position-relative my-auto" style="z-index: 1;">
        <div class="row justify-content-center">
          <div class="col-12 col-sm-10 col-md-8 col-lg-6 col-xl-5">
            <div class="cp-content-card p-4 p-md-5 text-center">
              
              <!-- Stepper Indicator -->
              <div class="wizard-stepper mb-3">
                <span class="wizard-step-dot"></span>
                <span class="wizard-step-dot active"></span>
              </div>

              <!-- Creator Avatar Hero -->
              <div class="avatar-glow-wrapper mb-2">
                <img v-if="!isAvatarMissing" :src="imageURL" class="avatar-glow-img" @error="imageLoadError">
                <img v-else src="@/assets/images/default_user.png" class="avatar-glow-img">
                <div class="avatar-badge"><i class="fa fa-check"></i></div>
              </div>

              <div class="text-white font-weight-700 font-size-16 mb-1">
                @{{ invitee.name }}
              </div>
              <h3 class="text-white font-weight-700 mb-2">Unlock Exclusive Access</h3>

              <!-- Perks Checklist -->
              <div class="perk-checklist mb-3">
                <div class="perk-item">
                  <div class="perk-icon"><i class="fa fa-check"></i></div>
                  <span>Behind the scenes access & exclusive feed</span>
                </div>
                <div class="perk-item">
                  <div class="perk-icon"><i class="fa fa-check"></i></div>
                  <span>Direct 1-on-1 messaging line</span>
                </div>
                <div class="perk-item">
                  <div class="perk-icon"><i class="fa fa-check"></i></div>
                  <span>Full access to bonus photos & videos</span>
                </div>
              </div>

              <global-message></global-message>

              <!-- Subscription Pricing Toggle -->
              <SnapchatSub v-if="isSnapchat || isTikTok" :invitee="invitee" @monthly="monthly" />
              <InstagramSub v-else :invitee="invitee" @monthly="monthly" />

              <!-- Terms & Checkout Button -->
              <div class="mb-3 text-center" style="font-size: 12px; color: rgba(255, 255, 255, 0.55); line-height: 1.5;">
                By subscribing, you agree to our
                <router-link :to="{name: 'TermsOfService'}" target="_blank" style="color: #70D5C2; text-decoration: underline;">Terms of Service</router-link>
                and recurring billing. Cancel anytime.
              </div>

              <button type="button" 
                      class="cp-btn-primary w-100"
                      @click="subscribe()" 
                      :disabled="paymentButtonClicked">
                <i class="fa fa-lock me-2"></i> Subscribe with Stripe
              </button>

              <!-- Trust & Security Badges -->
              <div class="trust-badge-container">
                <div class="trust-badge-item">
                  <i class="fa fa-shield-alt text-success me-1"></i> 256-Bit Encrypted
                </div>
                <div class="trust-badge-item">
                  <i class="fa fa-check-circle text-info me-1"></i> Cancel Anytime
                </div>
              </div>
              
              <div class="payment-methods-row">
                <span class="payment-badge"><i class="fa fa-shield me-1"></i>STRIPE SECURED</span>
                <span class="payment-badge">Apple Pay</span>
                <span class="payment-badge">Google Pay</span>
                <span class="payment-badge">Cards</span>
              </div>

            </div>
          </div>
        </div>
      </div>
    </section>

    <stripe-checkout v-if="show" ref="checkoutRef" :pk="pk" :sessionId="sessionId">
    </stripe-checkout>
  </div>
</template>

<script>

import { StripeCheckout } from '@vue-stripe/vue-stripe';
import GlobalMessage from "@/components/module/shared/GlobalMessage";
import LoadingModal from "@/components/module/shared/LoadingModal";
import { mapActions } from "vuex";
import { customerAccountMixin } from "@/mixin/customer-account/customer-account-mixin";
import HeaderHelp from "@/components/module/influencer/shared/HeaderHelp";
import SnapchatSub from "@/components/module/customer/account/SnapchatSubscription";
import InstagramSub from "@/components/module/customer/account/InstagramSubscription";
import titleMixin from "../../../../mixin/title-mixin";
import { paymentCancelled, SUBSCRIPTION } from "@/common/analytics-service";

export default {
  name: 'SnapchatSubscription',
  title: 'Subscription',
  mixins: [customerAccountMixin, titleMixin],
  components: {
    GlobalMessage, StripeCheckout,
    LoadingModal, HeaderHelp, SnapchatSub, InstagramSub
  },
  data() {
    return {
      invitee: { name: '' },
      isMonthly: false,
      pk: "",
      sessionId: "",
      show: false,
      paymentButtonClicked: false,
    }
  },
  created() {
    this.invitee = this.getInvitee;
    this.loadAvatarImg(this.invitee.urlSuffix);
    if (this.$route.query.cancelled) {
      this.setMessage('Payment was cancelled. You can choose a plan and retry checkout whenever you\'re ready.');
      paymentCancelled(this, SUBSCRIPTION, this.invitee);
      let clientReferenceId = this.$route.query.clientReferenceId;
      if (clientReferenceId) {
        let URL = `payment/api/v1/stripe/checkout/cancel/${clientReferenceId}`;
        this.postUnsecureData(URL, {}, () => {});
      }
    } else if (this.$route.query.error || this.$route.query.errorMessage) {
      this.pushGlobalError(this.$route.query.error || this.$route.query.errorMessage);
    } else if (this.$route.query.saved) {
      this.setMessage('SAVED.SUCCESSFULLY');
      this.$router.push({name: 'CustomerDirectMessage', params: {customerId:this.invitee.id}});
    }
  },
  methods: {
    ...mapActions(['setMessage', 'setSource', 'pushGlobalError']),

    subscribe() {
      if (this.paymentButtonClicked) return;
      this.paymentButtonClicked = true;

      const monthlyPrice = Number(this.invitee.monthlyPrice) || 10;
      const putData = {
        influencerId: this.invitee.id,
        type: this.isMonthly ? 'MONTHLY' : 'ANNUALLY',
        price: this.isMonthly ? monthlyPrice : monthlyPrice * 10,
        influencerPlatformId: this.getInfluencerPlatformId,
        source: this.getSource
      };

      this.setSource(null);
      const subscriptionUrl = `subscription/api/v1/customers/${this.getUser.id}/subscriptions/`;
      
      this.postUnsecureData(subscriptionUrl, putData, (data) => {
        const customerSubscription = data;
        if (!customerSubscription || !customerSubscription.id) {
          this.paymentButtonClicked = false;
          this.pushGlobalError('Unable to create subscription. Please try again.');
          return;
        }

        const subscriptionId = customerSubscription.id;
        const paymentUrl = `payment/api/v1/stripe/subscription/${subscriptionId}`;
        
        this.postUnsecureData(paymentUrl, {}, (stripeCheckoutData) => {
          if (stripeCheckoutData && stripeCheckoutData.othersData && stripeCheckoutData.req) {
            this.pk = stripeCheckoutData.othersData.pk;
            this.sessionId = stripeCheckoutData.req.id;
            this.show = true;
            this.$nextTick(() => {
              if (this.$refs.checkoutRef) {
                this.$refs.checkoutRef.redirectToCheckout();
              }
            });
          } else {
            this.paymentButtonClicked = false;
            this.pushGlobalError('Payment checkout initialization failed. Please try again.');
          }
        }, () => {
          this.paymentButtonClicked = false;
        });
      }, () => {
        this.paymentButtonClicked = false;
      });
    },
    monthly(bool) {
      this.isMonthly = bool;
    }
  }
}

</script>
<style scoped>
@media (min-width: 1200px) {
  .subsribe-button-margin {
    margin-left: 13%;
  }

  .subscribe-button-width{

    flex: 0 0 auto;
    width: 73%;
  }
}
</style>


<!--<style scoped>
.alert-snapchat-danger {
  border: 0;
  border-radius: 0;
  background: #FF7D7D;
}

.custom-control-label::before {
  background: transparent;
  border: 2px solid black;
}

.btn.customer-pricing-toggle:not(:disabled):not(.disabled).active {
  border: 2px solid #ff7d7d;
  background: #000;
}
.btn.customer-pricing-toggle:not(:disabled):not(.disabled).active > .customer-pricing{
  color: #FFFA37;
}
.btn.customer-pricing-toggle:not(:disabled):not(.disabled).active > .per-month{
  color: #FF7D7D !important;
  opacity: 0.5;
}
.btn.customer-pricing-toggle:not(:disabled):not(.disabled) {
  border: 2px solid #000;
}
.btn.focus, .btn:focus {
  outline: 0;
  box-shadow: none;
}
.form-outline {
  border: 1px solid #FFFA37 !important;
  border-radius: 18px;
}
.select-tags.active {
  background: #000;
  border: 0.2px solid #53535363;
  box-sizing: border-box;
  border-radius: 5px;
}
</style>-->
